import { apiSlice } from './apiSlice';
import {
  ActiveAffiliateUsersResponse,
  AffiliateDashboardResponse,
  AffiliateListResponse,
  AffiliateListWithStatusResponse,
  AffiliateSlab,
  AffiliateSubUsersResponse,
  SlabHistoryResponse,
  WeeklyCommissionAdminResponse,
} from '@/types';

export const affiliateApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllAffiliates: builder.query<
      AffiliateListResponse,
      {
        search?: string;
        fromDate?: string;
        toDate?: string;
        page?: number;
        limit?: number;
        requestStatus?: 'Pending' | 'Approved' | 'Rejected';
        sort?: string;
        direction?: 'asc' | 'desc';
      }
    >({
      query: ({ search, fromDate, toDate, page, limit, requestStatus, sort, direction } = {}) => ({
        url: '/affiliate/affiliates',
        method: 'GET',
        params: {
          ...(search && { search }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
          ...(page && { page }),
          ...(limit && { limit }),
          ...(requestStatus && { requestStatus }),
          ...(sort && { sort }),
          ...(direction && { direction }),
          ts: Date.now(),
        },
      }),
      providesTags: ['Affiliates'],
    }),

    getAffiliateDashboard: builder.query<AffiliateDashboardResponse, void>({
      query: () => ({
        url: '/affiliate',
        method: 'GET',
        params: {
          ts: Date.now(),
        },
      }),
      providesTags: ['Affiliates'],
    }),

    getAffiliatesListByStatus: builder.query<
      AffiliateListWithStatusResponse,
      {
        page?: number;
        limit?: number;
        search?: string;
        fromDate?: string;
        toDate?: string;
        status?: 'Active' | 'Inactive';
        sort?: string;
        direction?: 'asc' | 'desc';
      }
    >({
      query: ({ page, limit, search, fromDate, toDate, status, sort, direction } = {}) => ({
        url: '/affiliate/affiliates-list',
        method: 'GET',
        params: {
          ...(page && { page }),
          ...(limit && { limit }),
          ...(search && { search }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
          ...(status && { status }),
          ...(sort && { sort }),
          ...(direction && { direction }),
          ts: Date.now(),
        },
      }),
      providesTags: ['Affiliates'],
    }),

    getWeeklyCommissionAdmin: builder.query<
      WeeklyCommissionAdminResponse,
      {
        page?: number;
        limit?: number;
        search?: string;
        fromDate?: string;
        toDate?: string;
        status?: 'Pending' | 'Paid' | 'NotCommission';
        isExport?: boolean;
        platformId?: string;
        sort?: string;
        direction?: 'asc' | 'desc';
      }
    >({
      query: ({
        page,
        limit,
        search,
        fromDate,
        toDate,
        status,
        isExport,
        platformId,
        sort,
        direction,
      } = {}) => ({
        url: '/affiliate/weekly-commission/all',
        method: 'GET',
        params: {
          ...(page && { page }),
          ...(limit && { limit }),
          ...(search && { search }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
          ...(status && { status }),
          ...(isExport && { isExport }),
          ...(platformId ? { platformId } : {}),
          ...(sort && { sort }),
          ...(direction && { direction }),
          ts: Date.now(),
        },
      }),
      providesTags: ['Affiliates'],
    }),

    updateAffiliateStatus: builder.mutation<
      {
        message: string;
        data: any;
      },
      {
        id: string;
        requestStatus: 'Approved' | 'Rejected';
        reasonTo?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/affiliate/status/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Affiliates'],
    }),

    getAffiliateSlabs: builder.query<AffiliateSlab[], void>({
      query: () => ({
        url: '/affiliate/all-range',
        method: 'GET',
        params: {
          ts: Date.now(),
        },
      }),
      providesTags: ['AffiliateSlabs'],
    }),

    addAffiliateSlab: builder.mutation<
      { message: string; data: AffiliateSlab },
      {
        fromUser: number;
        toUser: number;
        percentage: number;
      }
    >({
      query: (body) => ({
        url: '/affiliate/create-range',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AffiliateSlabs', 'AffiliateSlabHistory'],
    }),

    updateAffiliateSlab: builder.mutation<
      { message: string; data: AffiliateSlab },
      {
        id: number;
        fromUser: number;
        toUser: number;
        percentage: number;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/affiliate/update-range/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AffiliateSlabs', 'AffiliateSlabHistory'],
    }),

    getAffiliateSlabHistory: builder.query<
      SlabHistoryResponse,
      { page?: number; limit?: number; sort?: string; direction?: 'asc' | 'desc' }
    >({
      query: ({ page = 1, limit = 10, sort, direction }) => ({
        url: '/affiliate/range-history',
        method: 'GET',
        params: {
          page,
          limit,
          ...(sort && { sort }),
          ...(direction && { direction }),
          ts: Date.now(),
        },
      }),
      providesTags: ['AffiliateSlabHistory'],
    }),

    getAffiliateSubUsers: builder.query<
      AffiliateSubUsersResponse,
      {
        affiliateId: number;
        page?: number;
        limit?: number;
        search?: string;
        status?: 'Active' | 'Inactive';
        sort?: string;
        direction?: 'asc' | 'desc';
      }
    >({
      query: ({ affiliateId, page = 1, limit = 10, search, status, sort, direction }) => ({
        url: `/affiliate/affiliates-list/${affiliateId}`,
        method: 'GET',
        params: {
          page,
          limit,
          ...(search && { search }),
          ...(status && { status }),
          ...(sort && { sort }),
          ...(direction && { direction }),
          ts: Date.now(),
        },
      }),
      providesTags: ['Affiliates'],
    }),

    getAffiliateActiveUsers: builder.query<
      ActiveAffiliateUsersResponse,
      {
        weeklyReportId: number;
        page?: number;
        limit?: number;
        search?: string;
        sort?: string;
        direction?: 'asc' | 'desc';
      }
    >({
      query: ({ weeklyReportId, page = 1, limit = 10, search, sort, direction }) => ({
        url: `/affiliate/active-user/${weeklyReportId}`,
        method: 'GET',
        params: {
          page,
          limit,
          ...(search && { search }),
          ...(sort && { sort }),
          ...(direction && { direction }),
          ts: Date.now(),
        },
      }),
      providesTags: ['Affiliates'],
    }),
  }),
});

export const {
  useGetAllAffiliatesQuery,
  useGetAffiliateDashboardQuery,
  useGetAffiliatesListByStatusQuery,
  useGetWeeklyCommissionAdminQuery,
  useUpdateAffiliateStatusMutation,
  useGetAffiliateSlabsQuery,
  useAddAffiliateSlabMutation,
  useUpdateAffiliateSlabMutation,
  useGetAffiliateSlabHistoryQuery,
  useGetAffiliateSubUsersQuery,
  useGetAffiliateActiveUsersQuery,
} = affiliateApi;
